import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdRestartAlt, MdSave } from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppTextarea,
  OutlineButton,
  PageLoader,
  PrimaryButton,
  StatusAlertCard,
} from '../../../components/ui';
import {
  isConfigFieldVisible,
  SYSTEM_CONFIG_DEFS,
  validateConfigValue,
} from '../../../data/system/systemConfig';
import {
  getSystemConfig,
  resetSystemConfig,
  updateSystemConfig,
} from '../../../repositories/systemConfig.repository';
import { logCurrentStaffActivity } from '../../../utils/activityLogHelper';
import { useStaffAccess } from '../../../utils/staffAccess';

function toDraft(config) {
  return SYSTEM_CONFIG_DEFS.reduce((acc, def) => {
    if (def.type === 'boolean') acc[def.key] = Boolean(config[def.key]);
    else acc[def.key] = String(config[def.key] ?? '');
    return acc;
  }, {});
}

const GROUP_ORDER = ['Vendors', 'Indents', 'General'];

export default function SystemConfigPage() {
  const toast = useToast();
  const { canEditSystemConfig } = useStaffAccess();
  const canEdit = canEditSystemConfig();
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await getSystemConfig();
        if (!cancelled) setDraft(toDraft(config));
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load configuration',
            status: 'error',
            position: 'top-right',
          });
          setDraft(toDraft({}));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const groups = useMemo(() => {
    const map = new Map();
    SYSTEM_CONFIG_DEFS.forEach((def) => {
      if (!map.has(def.group)) map.set(def.group, []);
      map.get(def.group).push(def);
    });
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => [g, map.get(g)]);
  }, []);

  const patch = (key, value) => {
    setDraft((d) => {
      const next = { ...d, [key]: value };
      if (key === 'vendorSelfRegistrationEnabled' && value) {
        next.vendorSelfRegistrationDisabledReason = '';
      }
      return next;
    });
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    const nextErrors = {};
    const patchValues = {};
    SYSTEM_CONFIG_DEFS.forEach((def) => {
      const result = validateConfigValue(def.key, draft[def.key], draft);
      if (!result.ok) nextErrors[def.key] = result.error;
      else patchValues[def.key] = result.value;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Fix the highlighted settings',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await updateSystemConfig(patchValues);
      setDraft(toDraft(saved));
      logCurrentStaffActivity({
        actionType: 'UPDATE',
        module: 'Master Data',
        summary: 'Updated system configuration',
        entityType: 'system_config',
        entityId: 'SYSTEM',
      });
      toast({
        title: 'System configuration saved',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const reset = await resetSystemConfig();
      setDraft(toDraft(reset));
      setErrors({});
      toast({
        title: 'Reset to defaults',
        status: 'info',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to reset',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (def) => {
    if (!draft || !isConfigFieldVisible(def, draft)) return null;

    if (def.type === 'boolean') {
      return (
        <Flex
          key={def.key}
          p={4}
          bg="bg.input"
          borderRadius="sm"
          borderWidth="1px"
          borderColor="border.subtle"
          align="center"
          justify="space-between"
          gap={4}
          h="100%"
        >
          <Box minW={0}>
            <Text fontWeight="600" fontSize="14px">
              {def.label}
            </Text>
            <Text fontSize="12px" color="text.secondary" mt={1}>
              {def.description}
            </Text>
          </Box>
          <Switch
            colorScheme="yellow"
            isChecked={Boolean(draft[def.key])}
            isDisabled={!canEdit || saving}
            onChange={(e) => patch(def.key, e.target.checked)}
          />
        </Flex>
      );
    }

    if (def.type === 'text') {
      return (
        <Box key={def.key}>
          <AppTextarea
            label={def.label}
            isRequired={Boolean(def.requiredWhen)}
            value={draft[def.key]}
            isReadOnly={!canEdit}
            onChange={(e) => patch(def.key, e.target.value)}
            rows={3}
            maxLength={def.maxLength}
            isInvalid={!!errors[def.key]}
            errorMessage={errors[def.key]}
            helperText={def.description}
          />
        </Box>
      );
    }

    return (
      <AppInput
        key={def.key}
        label={def.label}
        value={draft[def.key]}
        isReadOnly={!canEdit}
        inputMode="numeric"
        onChange={(e) =>
          patch(def.key, e.target.value.replace(/\D/g, '').slice(0, 4))
        }
        isInvalid={!!errors[def.key]}
        errorMessage={errors[def.key]}
        helperText={`${def.description} (${def.min}–${def.max})`}
      />
    );
  };

  if (loading || !draft) {
    return <PageLoader label="Loading system configuration…" />;
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Heading size={{ base: 'lg', xl: 'xl' }}>System configuration</Heading>
          <Text color="text.secondary" mt={2} maxW="720px">
            Business rules and feature toggles used across the app — change them here
            instead of in code.
          </Text>
        </Box>
        {canEdit ? (
          <Flex gap={2} wrap="wrap">
            <OutlineButton
              size="sm"
              leftIcon={<MdRestartAlt />}
              onClick={handleReset}
              isLoading={saving}
            >
              Reset defaults
            </OutlineButton>
            <PrimaryButton
              size="sm"
              leftIcon={<MdSave />}
              onClick={handleSave}
              isLoading={saving}
            >
              Save changes
            </PrimaryButton>
          </Flex>
        ) : null}
      </Flex>

      {!canEdit ? (
        <Box mb={6}>
          <StatusAlertCard
            title="Read only"
            body="You can view these settings but need the System configuration edit right to change them."
          />
        </Box>
      ) : null}

      <VStack align="stretch" spacing={4}>
        {groups.map(([group, defs]) => (
          <AppCard key={group}>
            <Heading size="sm" mb={4}>
              {group}
            </Heading>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              {defs.map((def) => renderField(def))}
            </SimpleGrid>
          </AppCard>
        ))}
      </VStack>
    </Box>
  );
}
